import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../context/TranslationContext';
import {
  getPlastisolMeters,
  clampPlastisolMeters,
  PLATISOL_MIN_METERS,
  PLATISOL_MAX_METERS,
  PLATISOL_DEFAULT_METERS,
} from '../lib/pdfPricing';

/** Linear meter entry — Plastisol Zetwerk is priced per meter (€125/m). */
const PlastisolDrawingBoard = ({
  meters: metersProp,
  width = 1500,
  height = 1500,
  onMetersChange,
}) => {
  const { t } = useTranslation();
  const [metersInput, setMetersInput] = useState('');
  const metersFocusedRef = useRef(false);

  const currentMeters = getPlastisolMeters({
    meters: metersProp,
    width,
    height,
  }) || PLATISOL_DEFAULT_METERS;

  useEffect(() => {
    if (!metersFocusedRef.current) {
      setMetersInput(
        currentMeters > 0 ? String(Number(currentMeters.toFixed(2))) : ''
      );
    }
  }, [currentMeters]);

  const formatMetersDisplay = (m) =>
    m > 0 ? String(Number(Number(m).toFixed(2))) : '';

  const commitMeters = () => {
    if (!onMetersChange) return;

    const parsed = parseFloat(String(metersInput).replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setMetersInput(formatMetersDisplay(currentMeters));
      return;
    }

    const next = clampPlastisolMeters(parsed);
    setMetersInput(formatMetersDisplay(next));
    onMetersChange(next);
  };

  return (
    <div className="flex h-full min-h-[500px] w-full items-center justify-center bg-[#F5F5F5] p-4 sm:p-8">
      <div className="flex w-full max-w-lg flex-col items-center rounded-xl border border-gray-200 bg-white px-8 py-10 shadow-sm sm:px-12 sm:py-12">
        <p className="mb-4 text-sm font-medium text-gray-600">{t('drawing.lengthMeters')}</p>
        <div className="flex items-baseline justify-center gap-3 sm:gap-4">
          <input
            type="number"
            min={PLATISOL_MIN_METERS}
            max={PLATISOL_MAX_METERS}
            step="0.01"
            value={metersInput}
            aria-label={t('drawing.lengthMeters')}
            onChange={(e) => setMetersInput(e.target.value)}
            onFocus={() => {
              metersFocusedRef.current = true;
            }}
            onBlur={() => {
              metersFocusedRef.current = false;
              commitMeters();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            className="min-w-[10rem] w-[11rem] flex-shrink-0 border-0 border-b-2 border-orange-500 bg-transparent px-1 text-center text-5xl font-bold tabular-nums text-gray-900 outline-none focus:border-orange-600 sm:min-w-[11rem] sm:w-[13rem] sm:text-6xl [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <span className="flex-shrink-0 text-3xl font-semibold text-gray-500 sm:text-4xl">m</span>
        </div>
        <p className="mt-6 text-center text-xs text-gray-500 sm:text-sm">
          {t('drawing.plastisolMetersHint')}
        </p>
      </div>
    </div>
  );
};

export default PlastisolDrawingBoard;
